import { TDeviceType } from '@cian/ui-kit';
import { IApplicationState } from '../../../types/redux';
import { TCaEventCreator, TCaEventDataSelector, TDataSelectors, TEventDataParser } from '../types';
import { getSiteType } from '../getSiteType';
import { curry, pipe } from 'ramda';
import { sendCaEvent } from '../sendCaEvent';
import { TProducts, TVacanciesList } from './types';
import { selectDeviceType } from '../../../selectors/page';
import { selectAllFilters, selectSelectedFilters, selectVacanciesGroup } from '../../../selectors/vacancies';
import { createLazySelector } from '../partialSelector';

type TOpenVacanciesEventExtra = TProducts & {
  page: {
    extra: TSearchConditions;
  };
};

type TOpenVacanciesEventData = {
  filters: TFilters;
  vacancies: TVacanciesList;
};

type TSelectedFilters = IApplicationState['vacancies']['selectedFilters'];
type TAllFilters = IApplicationState['vacancies']['filters'];
type TVacancies = IApplicationState['vacancies']['vacanciesGroup'];
type TFilter = {
  label: string;
  value: string;
};

type TFilterValue = string;

type TFilterName = keyof TSelectedFilters;

type TFilters = {
  [Property in TFilterName]: TFilterValue[];
};

type TSearchConditions = {
  SearchConditions: {
    filters: TFilters;
  };
};

type TOpenVacanciesRawData = {
  deviceType: TDeviceType;
  vacancies: TVacancies;
  selectedFilters: TSelectedFilters;
  allFilters: TAllFilters;
};
const createEvent: TCaEventCreator<TOpenVacanciesEventExtra, TOpenVacanciesEventData> = props => ({
  name: 'newpage',
  category: 'page',
  action: 'newpage',
  label: 'open',
  page: {
    pageType: 'ListingVac',
    listingType: 'list',
    siteType: props.siteType,
    extra: {
      SearchConditions: { filters: props.filters },
    },
  },
  products: props.vacancies,
});

const parseVacancies = (vacancies: TVacancies): TVacanciesList =>
  vacancies
    ?.map(vg => vg.vacancies)
    .flat()
    .map(v => ({ id: v.id })) || [];

const findFilterLabel = (filterValue: string, allFilters: TFilter[]): string | undefined =>
  allFilters.find(f => f.value === filterValue)?.label;

const parseFilters = (selectedFilters: TSelectedFilters, allFilters: TAllFilters): TFilters => {
  if (!allFilters) {
    return {} as TFilters;
  }

  const entries = Object.entries<string[]>(selectedFilters as TFilters);

  return entries.reduce<TFilters>((filters, [filterName, filterValues]) => {
    if (filterName === 'tags') {
      filters[filterName] = filterValues.map(fv => findFilterLabel(fv, allFilters.tagFilters) || fv);

      return filters;
    }

    const filter = allFilters.filters.find(f => f.name === filterName);

    filters[filterName as TFilterName] = filter
      ? filterValues.map(fv => findFilterLabel(fv, filter.options) || fv)
      : filterValues;

    return filters;
  }, {} as TFilters);
};

const parseOpenVacanciesEventData: TEventDataParser<TOpenVacanciesRawData, TOpenVacanciesEventData> = rawData => {
  return {
    siteType: getSiteType(rawData.deviceType),
    vacancies: parseVacancies(rawData.vacancies),
    filters: parseFilters(rawData.selectedFilters, rawData.allFilters),
  };
};

const selectors: TDataSelectors<TOpenVacanciesRawData> = {
  deviceType: selectDeviceType,
  vacancies: selectVacanciesGroup,
  allFilters: selectAllFilters,
  selectedFilters: selectSelectedFilters,
};

const selectEventData: TCaEventDataSelector<TOpenVacanciesRawData> = state => ({
  deviceType: selectDeviceType(state),
  vacancies: selectVacanciesGroup(state),
  selectedFilters: selectSelectedFilters(state),
  allFilters: selectAllFilters(state),
});

// TODO Сделать на ООП

const _createLazySelector = curry(createLazySelector)(selectors);

const _sendEvent = pipe(parseOpenVacanciesEventData, createEvent, sendCaEvent);

const sendEventWithDataFromStore = pipe(selectEventData, _sendEvent);

export const sendOpenListingPageEvent = (partialData?: Partial<TOpenVacanciesRawData>) => {
  if (!partialData) {
    return sendEventWithDataFromStore;
  }

  const partialEventDataSelector = _createLazySelector(partialData);

  return pipe(partialEventDataSelector, _sendEvent);
};
